import {Box, Button, ButtonGroup, Popover, Stack, Typography} from '@mui/material'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import dayjs, {Dayjs} from 'dayjs'
import 'dayjs/locale/fr'
import React, {useState} from 'react'

dayjs.locale('fr')

export interface PeriodSelectorProps {
    selectedPeriod: '30d' | '90d' | '12m' | '24m' | 'custom'
    startDate?: Dayjs
    endDate?: Dayjs
    onPeriodChange: (period: '30d' | '90d' | '12m' | '24m' | 'custom', startDate?: Dayjs, endDate?: Dayjs) => void
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
    selectedPeriod,
    startDate,
    endDate,
    onPeriodChange,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(startDate || dayjs().subtract(30, 'day'))
    const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(endDate || dayjs())

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handlePeriodSelect = (period: '30d' | '90d' | '12m' | '24m' | 'custom') => {
        if (period === 'custom') {
            // Don't close popover for custom selection
            onPeriodChange(period, tempStartDate || undefined, tempEndDate || undefined)
        } else {
            onPeriodChange(period)
            handleClose()
        }
    }

    const handleCustomDateApply = () => {
        if (tempStartDate && tempEndDate) {
            onPeriodChange('custom', tempStartDate, tempEndDate)
            handleClose()
        }
    }

    const getPeriodLabel = () => {
        switch (selectedPeriod) {
            case '30d':
                return '30j'
            case '90d':
                return '90j'
            case '12m':
                return '12m'
            case '24m':
                return '24m'
            case 'custom':
                if (startDate && endDate) {
                    return `${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`
                }
                return 'Custom'
            default:
                return '30j'
        }
    }

    const open = Boolean(anchorEl)

    return (
        <>
            <Button
                onClick={handleClick}
                size="small"
                startIcon={<CalendarTodayIcon sx={{fontSize: 12}} />}
                sx={{
                    fontSize: '10px',
                    padding: '2px 8px',
                    minWidth: 'auto',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#64748b',
                    border: 'none',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    }
                }}
            >
                {getPeriodLabel()}
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        padding: '12px',
                        minWidth: '200px',
                    }
                }}
            >
                <Stack spacing={2}>
                    <Typography variant="caption" sx={{fontWeight: 'bold', color: '#374151'}}>
                        Période
                    </Typography>
                    
                    <ButtonGroup
                        orientation="vertical"
                        size="small"
                        variant="outlined"
                        sx={{
                            '& .MuiButton-root': {
                                fontSize: '11px',
                                padding: '4px 12px',
                                justifyContent: 'flex-start',
                            }
                        }}
                    >
                        <Button
                            onClick={() => handlePeriodSelect('30d')}
                            variant={selectedPeriod === '30d' ? 'contained' : 'outlined'}
                        >
                            30 derniers jours
                        </Button>
                        <Button
                            onClick={() => handlePeriodSelect('90d')}
                            variant={selectedPeriod === '90d' ? 'contained' : 'outlined'}
                        >
                            90 derniers jours
                        </Button>
                        <Button
                            onClick={() => handlePeriodSelect('12m')}
                            variant={selectedPeriod === '12m' ? 'contained' : 'outlined'}
                        >
                            12 derniers mois
                        </Button>
                        <Button
                            onClick={() => handlePeriodSelect('24m')}
                            variant={selectedPeriod === '24m' ? 'contained' : 'outlined'}
                        >
                            24 derniers mois
                        </Button>
                        <Button
                            onClick={() => handlePeriodSelect('custom')}
                            variant={selectedPeriod === 'custom' ? 'contained' : 'outlined'}
                        >
                            Période personnalisée
                        </Button>
                    </ButtonGroup>

                    {selectedPeriod === 'custom' && (
                        <Box sx={{paddingTop: '8px', borderTop: '1px solid #e5e7eb'}}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                                <Stack spacing={2}>
                                    <DatePicker
                                        label="Date de début"
                                        value={tempStartDate}
                                        onChange={(newValue) => setTempStartDate(newValue)}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { fontSize: '11px' }
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="Date de fin"
                                        value={tempEndDate}
                                        onChange={(newValue) => setTempEndDate(newValue)}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { fontSize: '11px' }
                                            }
                                        }}
                                    />
                                    <Button
                                        onClick={handleCustomDateApply}
                                        variant="contained"
                                        size="small"
                                        disabled={!tempStartDate || !tempEndDate}
                                        sx={{ fontSize: '11px' }}
                                    >
                                        Appliquer
                                    </Button>
                                </Stack>
                            </LocalizationProvider>
                        </Box>
                    )}
                </Stack>
            </Popover>
        </>
    )
}

export default PeriodSelector